/** Route paths for the main app pages */
export enum MainPath {
  home = '/',
  notFound = '/404',
  explore = '/explore',
  journal = '/journal',
  today = '/today',
  consentRequired = '/consent-required',
}

/**
 * The query that opens Explore on the reader's bookmarks, with its "Saved" filter already on
 * (IPD-3328). A query rather than a hash: it selects what the page shows rather than naming a place
 * on it.
 */
export const EXPLORE_SAVED_QUERY = {saved: '1'} as const;

/** Route paths for content pages (recommendations, challenges) */
export enum ContentPath {
  recommendation = 'recommendation/:contentType/:catalogId/:itemId',
  challenge = 'challenge/:id',
  challengeSession = 'challenge/:id/session/:sessionId',
}

/** Route paths for unauthenticated public pages */
export enum PublicPath {
  public = '/public',
  continueAuth = '/public/continue-auth',
  signInRedirect = '/public/sign-in-redirect',
  signUp = '/public/sign-up',
  verifyEmail = '/public/verify-email',
  login = '/public/login',
  instructionsForUse = '/instructions-for-use',
}

/** Route paths for user account and settings pages */
export enum AccountPath {
  account = '/account',
  changePassword = '/change-password',
  subscription = '/account/subscription',
  profile = '/account/profile',
  security = '/account/security',
  preferences = '/account/preferences',
  badges = '/account/badges',
  debug = '/account/debug',
}

/** Route paths for intervention (path) pages */
export enum InterventionPath {
  interventionsList = '/paths',
  interventionFetch = '/path-:iid',
  interventionInfo = '/path-:iid/description',
  interventionInfoAbout = '/path-:iid/description/about',
  interventionInfoSessions = '/path-:iid/description/sessions',
  interventionRecap = '/path-:iid/recap',
  interventionLanding = '/path-:iid/landing',

}

/** Named route identifiers for account pages */
export enum AccountRouteName {
  profile = 'account-profile',
  subscription = 'account-subscription',
  security = 'account-security',
  preferences = 'account-preferences',
  badges = 'account-badges',
  debug = 'account-debug',
}

/** Named route identifiers for intervention pages */
export enum InterventionRouteName {
  interventionInfo = 'interventionInfo',
}

/** Route paths for intervention session pages */
export enum SessionPath {
  sessionWrapper = '/path-:iid/session-:sid',
  sessionPage = '/path-:iid/session-:sid/page-:pid',
  sessionCompletedPage = '/path-:iid/session-:sid/completed',
}

/** Route paths for topic pages */
export enum TopicPath {
  topic = '/topic/:topic',
  // The partner band's "view all". Not per-partner: the band is the organisation's whole pack,
  // however many partners that is, so the list it opens is that same set.
  partnerContent = '/partner-content',
}

/** Aggregated path enums for all route sections of the application */
export interface PagesPath {
  intervention: typeof InterventionPath;
  session: typeof SessionPath;
  main: typeof MainPath;
  public: typeof PublicPath;
  account: typeof AccountPath;
  topic: typeof TopicPath;
  content: typeof ContentPath;
}


export const pagesPath: PagesPath = Object.freeze({
  main: MainPath,
  intervention: InterventionPath,
  session: SessionPath,
  public: PublicPath,
  account: AccountPath,
  topic: TopicPath,
  content: ContentPath,
});
